/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    initialized: false,
    filesInitialized: false,
    historyInitialized: false,
    order: {
        id: null,
        documentTypeId: '',
        folder: {
            id: 0,
            name: '',
            folderAttachments: [],
        },
        number: null,
        statusId: null,
        orderReturns: [],
    },
    finances: null,
    snapshot: null,
};

export const createOrderDetailsSlice = sliceName => {
    const orderDetailsSlice = createSlice({
        name: sliceName,
        initialState,
        reducers: {
            /**
             * Initializes the order store with order data
             * @param {Object} action.payload - Order data
             */
            init: (state, action) => {
                return {
                    ...state,
                    initialized: true,
                    order: action.payload,
                    filesInitialized: false,
                    historyInitialized: false,
                };
            },

            /**
             * Replaces the current order
             * @param {Object} action.payload - New order data
             */
            replace: (state, action) => {
                return {
                    ...state,
                    order: action.payload,
                };
            },

            /**
             * Updates order with partial data
             * @param {Object} action.payload - Partial order updates
             */
            update: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        ...action.payload,
                    },
                };
            },

            /**
             * Resets initialization flags for all pages
             */
            resetPages: () => {
                return {
                    ...initialState,
                    initialized: false,
                    filesInitialized: false,
                    historyInitialized: false,
                };
            },

            /**
             * Initializes a specific route
             * @param {string} action.payload - Route name (e.g., 'files', 'history')
             */
            initializeRoute: (state, action) => {
                return {
                    ...state,
                    [`${action.payload}Initialized`]: true,
                };
            },

            /**
             * Updates order key-value pairs
             * @param {Object} action.payload
             * @param {string|string[]} action.payload.key - Property key or array of keys
             * @param {*|*[]} action.payload.value - Value or array of values
             */
            updateKeyValuePair: (state, action) => {
                const { key, value } = action.payload;
                const update = {};

                if (Array.isArray(key)) {
                    key.forEach((k, idx) => {
                        update[k] = value[idx];
                    });
                } else {
                    update[key] = value;
                }

                return {
                    ...state,
                    order: { ...state.order, ...update },
                };
            },

            /**
             * Adds a note to the order job
             * @param {Object} action.payload - Note object
             */
            addNote: (state, action) => {
                const notes = state.order.job?.notes || [];
                return {
                    ...state,
                    order: {
                        ...state.order,
                        job: {
                            ...state.order.job,
                            notes: [...notes, action.payload],
                        },
                    },
                };
            },

            /**
             * Initializes financial information
             * @param {Object} action.payload - Financial data
             */
            initFinancialInformation: (state, action) => {
                return {
                    ...state,
                    finances: action.payload,
                };
            },

            /**
             * Updates payment information
             * @param {Object} action.payload
             * @param {Object} action.payload.order - Updated order
             * @param {Object} action.payload.snapshot - Payment snapshot
             */
            updatePayment: (state, action) => {
                return {
                    ...state,
                    initialized: true,
                    order: action.payload.order,
                    snapshot: action.payload.snapshot,
                };
            },

            /**
             * Updates billing information
             * @param {Object} action.payload
             * @param {Object} action.payload.order - Updated order
             * @param {Object} action.payload.snapshot - Billing snapshot
             */
            updateBilling: (state, action) => {
                return {
                    ...state,
                    initialized: true,
                    order: action.payload.order,
                    snapshot: action.payload.snapshot,
                };
            },

            /**
             * Updates an existing note
             * @param {Object} action.payload - Updated note object with id
             */
            updateNote: (state, action) => {
                const updatedNotes = state.order.job.notes.map(note =>
                    note.id === action.payload.id ? action.payload : note
                );
                return {
                    ...state,
                    order: {
                        ...state.order,
                        job: {
                            ...state.order.job,
                            notes: updatedNotes,
                        },
                    },
                };
            },

            /**
             * Updates special instructions
             * @param {string} action.payload - Special instructions text
             */
            updateSpecialInstructions: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        instructions: {
                            special: action.payload,
                        },
                    },
                };
            },

            /**
             * Adds a contact to the order
             * @param {Object} action.payload - Contact object
             */
            addContact: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        contacts: [
                            ...(state.order.contacts ?? []),
                            action.payload,
                        ],
                    },
                };
            },

            /**
             * Removes a contact from the order
             * @param {number} action.payload - Contact ID
             */
            removeContact: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        contacts: state.order.contacts.filter(
                            c => c.id !== action.payload
                        ),
                    },
                };
            },

            /**
             * Adds a comment to the order
             * @param {Object} action.payload - Comment object
             */
            addComment: (state, action) => {
                const comments = state.order.comments || [];
                return {
                    ...state,
                    order: {
                        ...state.order,
                        comments: [...comments, action.payload],
                    },
                };
            },

            /**
             * Updates an existing comment
             * @param {Object} action.payload - Updated comment object with id
             */
            updateComment: (state, action) => {
                const updatedComments = state.order.comments.map(comment =>
                    comment.id === action.payload.id ? action.payload : comment
                );
                return {
                    ...state,
                    order: {
                        ...state.order,
                        comments: updatedComments,
                    },
                };
            },

            /**
             * Adds a folder attachment to the order
             * @param {Object} action.payload - Folder attachment object
             */
            addFolderAttachment: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        folder: {
                            ...state.order.folder,
                            folderAttachments: [
                                ...state.order.folder.folderAttachments,
                                action.payload,
                            ],
                        },
                    },
                };
            },

            /**
             * Removes a folder attachment from the order
             * @param {string} action.payload - Folder attachment ID
             */
            removeFolderAttachment: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        folder: {
                            ...state.order.folder,
                            folderAttachments:
                                state.order.folder.folderAttachments.filter(
                                    fa => fa.id !== action.payload
                                ),
                        },
                    },
                };
            },

            /**
             * Updates fulfillment logistics
             * @param {Array} action.payload - Logistics array
             */
            updateFulfillmentLogistics: (state, action) => {
                return {
                    ...state,
                    order: {
                        ...state.order,
                        fulfillmentLogistics: action.payload,
                    },
                };
            },

            /**
             * Sets finance edit mode
             * @param {boolean} action.payload - Finance edit flag
             */
            setFinanceEdit: (state, action) => {
                const financeEdit = action.payload;
                return {
                    ...state,
                    order: {
                        ...state.order,
                        financeEdit,
                        originalLineItems: financeEdit
                            ? state.order.lineItems
                            : undefined,
                        lineItems: financeEdit
                            ? state.order.lineItems
                            : state.order.originalLineItems,
                    },
                };
            },

            /**
             * Updates fulfillment details
             * @param {Object} action.payload
             * @param {Object} action.payload.fulfillmentDetails - Fulfillment details object
             * @param {Object} action.payload.fulfillmentDistributionCenter - Distribution center
             */
            updateFulfillmentDetails: (state, action) => {
                const { fulfillmentDetails, fulfillmentDistributionCenter } =
                    action.payload;

                const updates = {
                    fulfillmentTypeId: fulfillmentDetails?.fulfillmentTypeId,
                    fulfillmentDistributionCenterId:
                        fulfillmentDetails?.fulfillmentDistributionCenterId,
                    fulfillmentToAddress:
                        fulfillmentDetails?.fulfillmentToAddress,
                    fulfillmentToCity: fulfillmentDetails?.fulfillmentToCity,
                    fulfillmentToRegion:
                        fulfillmentDetails?.fulfillmentToRegion,
                    fulfillmentToPostalCode:
                        fulfillmentDetails?.fulfillmentToPostalCode,
                    fulfillmentTo: fulfillmentDetails?.fulfillmentTo,
                    residentialDelivery:
                        fulfillmentDetails?.residentialDelivery,
                    vendorDistributionCenter: fulfillmentDistributionCenter,
                };

                return {
                    ...state,
                    order: {
                        ...state.order,
                        ...updates,
                    },
                };
            },
        },
    });

    return {
        reducer: orderDetailsSlice.reducer,
        actions: orderDetailsSlice.actions,
    };
};
